// src/components/agencies/OpeningHoursPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchAgency,
  fetchOpeningHours,
  updateOpeningHours,
  clearAgencyError,
  clearAgencySuccess,
} from '../../store/actions/agencyActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const OpeningHoursPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { selectedAgency, loading, error, success, openingHours } = useSelector(
    (state) => state.agencies
  );

  const [hours, setHours] = useState({
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: '',
    saturday: '',
    sunday: '',
  });
  
  const [originalHours, setOriginalHours] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [activeDay, setActiveDay] = useState('monday');

  const days = [
    { key: 'monday', label: 'Lundi', shortLabel: 'Lun' },
    { key: 'tuesday', label: 'Mardi', shortLabel: 'Mar' },
    { key: 'wednesday', label: 'Mercredi', shortLabel: 'Mer' },
    { key: 'thursday', label: 'Jeudi', shortLabel: 'Jeu' },
    { key: 'friday', label: 'Vendredi', shortLabel: 'Ven' },
    { key: 'saturday', label: 'Samedi', shortLabel: 'Sam' },
    { key: 'sunday', label: 'Dimanche', shortLabel: 'Dim' },
  ];

  // Time presets for quick selection
  const timePresets = [
    { label: 'Fermé', value: 'Fermé' },
    { label: '09:00 - 17:00', value: '09:00 - 17:00' },
    { label: '09:00 - 18:00', value: '09:00 - 18:00' },
    { label: '09:00 - 19:00', value: '09:00 - 19:00' },
    { label: '08:00 - 20:00', value: '08:00 - 20:00' },
    { label: '10:00 - 19:00', value: '10:00 - 19:00' },
    { label: '24/7', value: '24/7' },
    { label: 'Sur rendez-vous', value: 'Sur rendez-vous' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1024px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    agencyName: {
      fontSize: '1rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      overflowX: 'auto',
    },
    tab: {
      padding: '1rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
      backgroundColor: '#ffffff',
    },
    mainContent: {
      padding: '2rem',
    },
    hoursCard: {
      maxWidth: '600px',
      margin: '0 auto',
    },
    dayHeader: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    timeInputGroup: {
      marginBottom: '2rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      fontSize: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    presetsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '0.75rem',
      marginTop: '1rem',
    },
    presetButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    presetButtonHover: {
      backgroundColor: '#e5e7eb',
      borderColor: '#d1d5db',
    },
    presetButtonSelected: {
      backgroundColor: '#dbeafe',
      borderColor: '#2563eb',
      color: '#2563eb',
    },
    weekPreview: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    weekPreviewTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    weekGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '0.5rem',
    },
    dayPreview: {
      textAlign: 'center',
    },
    dayPreviewLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    dayPreviewTime: {
      fontSize: '0.875rem',
      color: '#374151',
      padding: '0.25rem',
      borderRadius: '0.25rem',
    },
    dayPreviewClosed: {
      color: '#dc2626',
    },
    copyOptions: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    copyTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    copyButtons: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    copyButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    copyButtonHover: {
      backgroundColor: '#f3f4f6',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    cancelButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    saveButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    saveButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    saveButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchAgency(id));
      dispatch(fetchOpeningHours(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (openingHours) {
      const hoursData = {
        monday: openingHours.monday || 'Fermé',
        tuesday: openingHours.tuesday || 'Fermé',
        wednesday: openingHours.wednesday || 'Fermé',
        thursday: openingHours.thursday || 'Fermé',
        friday: openingHours.friday || 'Fermé',
        saturday: openingHours.saturday || 'Fermé',
        sunday: openingHours.sunday || 'Fermé',
      };
      setHours(hoursData);
      setOriginalHours(hoursData);
    }
  }, [openingHours]);

  const validateHours = useCallback(() => {
    const newErrors = {};
    const timeRegex = /^(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})$/;

    Object.entries(hours).forEach(([day, value]) => {
      if (value !== 'Fermé' && value !== '24/7' && value !== 'Sur rendez-vous') {
        if (!timeRegex.test(value)) {
          newErrors[day] = 'Format invalide. Utilisez "HH:MM - HH:MM"';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [hours]);

  const handleHourChange = (day, value) => {
    setHours(prev => ({
      ...prev,
      [day]: value,
    }));
    // Clear error for this day
    if (errors[day]) {
      setErrors(prev => ({
        ...prev,
        [day]: undefined,
      }));
    }
  };

  const handlePresetSelect = (day, preset) => {
    handleHourChange(day, preset);
  };

  const handleCopyToAll = () => {
    const currentValue = hours[activeDay];
    const newHours = {};
    days.forEach(day => {
      newHours[day.key] = currentValue;
    });
    setHours(newHours);
  };

  const handleCopyToWeekdays = () => {
    const currentValue = hours[activeDay];
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const newHours = { ...hours };
    weekdays.forEach(day => {
      newHours[day] = currentValue;
    });
    setHours(newHours);
  };

  const handleCopyToWeekend = () => {
    const currentValue = hours[activeDay];
    const weekend = ['saturday', 'sunday'];
    const newHours = { ...hours };
    weekend.forEach(day => {
      newHours[day] = currentValue;
    });
    setHours(newHours);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateHours()) {
      return;
    }

    setIsSubmitting(true);

    try {
        console.log(id, hours);
        
      await dispatch(updateOpeningHours(id, hours));
      setOriginalHours(hours);
      // Show success message and redirect after a short delay
      setTimeout(() => {
        navigate(`/admin/agencies/${id}/details`);
      }, 1500);
    } catch (error) {
      console.error('Failed to update opening hours:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, id, hours, navigate, validateHours]);

  const handleCancel = () => {
    if (JSON.stringify(hours) !== JSON.stringify(originalHours)) {
      setShowCancelConfirm(true);
    } else {
      navigate(`/admin/agencies/${id}/details`);
    }
  };

  const hasChanges = JSON.stringify(hours) !== JSON.stringify(originalHours);

  if (loading && !openingHours) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={handleCancel}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>Horaires d'ouverture</h1>
            {selectedAgency && (
              <div style={styles.agencyName}>{selectedAgency.name}</div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearAgencyError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message="Horaires mis à jour avec succès"
          onClose={() => dispatch(clearAgencySuccess())}
        />
      )}

      <div style={styles.content}>
        {/* Day Tabs */}
        <div style={styles.tabs}>
          {days.map((day) => (
            <button
              key={day.key}
              style={{
                ...styles.tab,
                ...(activeDay === day.key ? styles.tabActive : {}),
              }}
              onClick={() => setActiveDay(day.key)}
            >
              {day.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.mainContent}>
            <div style={styles.hoursCard}>
              {/* Active Day Hours */}
              <div style={styles.dayHeader}>
                {days.find(d => d.key === activeDay)?.label}
              </div>

              <div style={styles.timeInputGroup}>
                <label style={styles.label} htmlFor={activeDay}>
                  Horaires
                </label>
                <input
                  type="text"
                  id={activeDay}
                  value={hours[activeDay]}
                  onChange={(e) => handleHourChange(activeDay, e.target.value)}
                  placeholder="Ex: 09:00 - 18:00"
                  style={{
                    ...styles.input,
                    ...(errors[activeDay] ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = styles.inputFocus.borderColor;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = styles.input.borderColor;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors[activeDay] && (
                  <div style={styles.errorText}>{errors[activeDay]}</div>
                )}
              </div>

              {/* Time Presets */}
              <div>
                <label style={styles.label}>Presets</label>
                <div style={styles.presetsGrid}>
                  {timePresets.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handlePresetSelect(activeDay, preset.value)}
                      style={{
                        ...styles.presetButton,
                        ...(hours[activeDay] === preset.value ? styles.presetButtonSelected : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (hours[activeDay] !== preset.value) {
                          e.currentTarget.style.backgroundColor = styles.presetButtonHover.backgroundColor;
                          e.currentTarget.style.borderColor = styles.presetButtonHover.borderColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hours[activeDay] !== preset.value) {
                          e.currentTarget.style.backgroundColor = styles.presetButton.backgroundColor;
                          e.currentTarget.style.borderColor = styles.presetButton.borderColor;
                        }
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Week Preview */}
              <div style={styles.weekPreview}>
                <div style={styles.weekPreviewTitle}>Aperçu de la semaine</div>
                <div style={styles.weekGrid}>
                  {days.map((day) => (
                    <div key={day.key} style={styles.dayPreview}>
                      <div style={styles.dayPreviewLabel}>{day.shortLabel}</div>
                      <div
                        style={{
                          ...styles.dayPreviewTime,
                          ...(hours[day.key] === 'Fermé' ? styles.dayPreviewClosed : {}),
                        }}
                      >
                        {hours[day.key] === 'Fermé' ? '❌' : hours[day.key]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Copy Options */}
              <div style={styles.copyOptions}>
                <div style={styles.copyTitle}>Copier les horaires</div>
                <div style={styles.copyButtons}>
                  <button
                    type="button"
                    onClick={handleCopyToAll}
                    style={styles.copyButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButtonHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButton.backgroundColor;
                    }}
                  >
                    Copier à tous les jours
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyToWeekdays}
                    style={styles.copyButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButtonHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButton.backgroundColor;
                    }}
                  >
                    Copier aux jours de semaine
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyToWeekend}
                    style={styles.copyButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButtonHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = styles.copyButton.backgroundColor;
                    }}
                  >
                    Copier au week-end
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={styles.cancelButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !hasChanges}
                  style={{
                    ...styles.saveButton,
                    ...((isSubmitting || !hasChanges) ? styles.saveButtonDisabled : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting && hasChanges) {
                      e.currentTarget.style.backgroundColor = styles.saveButtonHover.backgroundColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting && hasChanges) {
                      e.currentTarget.style.backgroundColor = styles.saveButton.backgroundColor;
                    }
                  }}
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer les horaires'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate(`/admin/agencies/${id}/details`)}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default OpeningHoursPage;